import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/shared/components/ui/dialog'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { cn } from '@/shared/lib/utils'
import { Eye, ImageIcon, LayoutGrid, Loader2, Upload, X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { useReferenceImagePresets } from '../hooks/useReferenceImagePresets'
import StoryboardImageLightbox from './StoryboardImageLightbox'

interface ReferenceImagePickerProps {
    value?: string
    usage?: string
    onChange: (url: string | undefined) => void
    onUsageChange?: (usage: string) => void
}

const ReferenceImagePicker = ({ value, usage, onChange, onUsageChange }: ReferenceImagePickerProps) => {
    const [isDragging, setIsDragging] = useState(false)
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [isPresetsDialogOpen, setIsPresetsDialogOpen] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const { categories, isLoading } = useReferenceImagePresets()

    const handleFile = useCallback(
        (file: File) => {
            if (!file.type.startsWith('image/')) return
            const reader = new FileReader()
            reader.onload = (e) => {
                const result = e.target?.result as string
                if (result) onChange(result)
            }
            reader.readAsDataURL(file)
        },
        [onChange]
    )

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault()
            setIsDragging(false)
            const file = e.dataTransfer.files[0]
            if (file) handleFile(file)
        },
        [handleFile]
    )

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) handleFile(file)
        e.target.value = ''
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Reference Image
                </label>
            </div>

            <Dialog open={isPresetsDialogOpen} onOpenChange={setIsPresetsDialogOpen}>
                {value ? (
                    <div className="relative rounded-lg overflow-hidden border border-primary/40 bg-muted/20 group aspect-video">
                        <img
                            src={value}
                            alt="Reference"
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button
                                variant="secondary"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => setIsPreviewOpen(true)}
                            >
                                <Eye className="h-4 w-4 mr-1.5" />
                                Preview
                            </Button>
                            <Button
                                variant="destructive"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => onChange(undefined)}
                            >
                                <X className="h-4 w-4 mr-1.5" />
                                Remove
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2 bg-background/20 border-white/20 text-white hover:bg-background/40"
                                onClick={() => setIsPresetsDialogOpen(true)}
                            >
                                <LayoutGrid className="h-4 w-4 mr-1.5" />
                                Presets
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        {/* Upload Zone */}
                        <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={cn(
                                'relative flex flex-col items-center justify-center gap-2 aspect-video rounded-lg border-2 border-dashed cursor-pointer transition-all',
                                isDragging
                                    ? 'border-primary bg-primary/10'
                                    : 'border-muted-foreground/20 bg-muted/10 hover:border-primary/40 hover:bg-muted/20'
                            )}
                        >
                            <Upload className="h-5 w-5 text-muted-foreground" />
                            <span className="text-[10px] font-medium text-muted-foreground">Upload</span>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleFileInput}
                            />
                        </div>

                        {/* Presets Trigger */}
                        <DialogTrigger asChild>
                            <div className="relative flex flex-col items-center justify-center gap-2 aspect-video rounded-lg border-2 border-dashed border-muted-foreground/20 bg-muted/10 hover:border-primary/40 hover:bg-muted/20 cursor-pointer transition-all">
                                <LayoutGrid className="h-5 w-5 text-muted-foreground" />
                                <span className="text-[10px] font-medium text-muted-foreground">Presets</span>
                            </div>
                        </DialogTrigger>
                    </div>
                )}

                <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 overflow-hidden">
                    <DialogHeader className="p-6 border-b">
                        <DialogTitle>Reference Image Presets</DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="flex-1 p-6">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : (
                            <div className="space-y-8 pb-4">
                                {categories.map((cat) => (
                                    <div key={cat.name} className="space-y-4">
                                        <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                            <span className="h-px bg-muted-foreground/20 flex-1"></span>
                                            {cat.name}
                                            <span className="h-px bg-muted-foreground/20 flex-1"></span>
                                        </h3>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                            {cat.presets.map((preset) => (
                                                <button
                                                    key={preset.id}
                                                    type="button"
                                                    onClick={() => {
                                                        onChange(preset.url)
                                                        onUsageChange?.(preset.prompt || cat.prompt || '')
                                                        setIsPresetsDialogOpen(false)
                                                    }}
                                                    className="group relative aspect-video rounded-lg overflow-hidden border border-border hover:border-primary transition-all shadow-sm hover:shadow-md"
                                                >
                                                    <img
                                                        src={preset.thumbnail}
                                                        alt={preset.label}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                        referrerPolicy="no-referrer"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                                                        <p className="text-white text-[10px] font-medium truncate">{preset.label}</p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ScrollArea>
                </DialogContent>
            </Dialog>

            {isPreviewOpen && value && (
                <StoryboardImageLightbox
                    imageUrl={value}
                    onClose={() => setIsPreviewOpen(false)}
                />
            )}

            {value && onUsageChange && (
                <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Reference Use
                    </label>
                    <Input
                        value={usage || ''}
                        onChange={(e) => onUsageChange(e.target.value)}
                        placeholder="Style, lighting, color palette, pose, character position..."
                    />
                    <p className="text-[11px] text-muted-foreground">
                        Describe how this image should guide generation, such as style, lighting,
                        composition, color, or character placement.
                    </p>
                </div>
            )}
        </div>
    )
}

export default ReferenceImagePicker
